import { Cascader, message } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { centroid } from '@turf/turf';
import { useTranslation } from 'react-i18next';
import RegionsService from '../../services/Regions';
import { GlobalContext } from '../../App';
import { BaseViewContext } from '../../views/Base';

const RegionsCascader = (props) => {
  const [regions, setRegions] = useState([]);
  const [selection, setSelection] = useState([]);
  const requestId = useRef(0);
  const expectedKey = useRef(null);
  const { setGlobalMapCenter, globalSelectedRegion, setGlobalSelectedRegion } = useContext(GlobalContext);
  const { user } = useContext(BaseViewContext);
  const { t } = useTranslation('translation');

  useEffect(() => {
    let alive = true;
    if (user?.organization) RegionsService.getParentRegions().then((result) => {
      if (alive) setRegions((result || []).filter((r) => ['tur', 'aze', 'rou', 'nld', 'svn', 'svk', 'pol', 'cze'].includes(r.value)));
    });
    return () => { alive = false; requestId.current += 1; };
  }, [user]);

  // A map click invalidates an older selector request too.
  useEffect(() => {
    if (props.ccMode && expectedKey.current !== globalSelectedRegion?.properties?.key) {
      requestId.current += 1;
      expectedKey.current = globalSelectedRegion?.properties?.key;
      setSelection([]);
    }
  }, [globalSelectedRegion, props.ccMode]);

  const onChange = async (value, selectedOptions) => {
    const id = ++requestId.current;
    setSelection(value || []);
    if (!value?.length) {
      expectedKey.current = undefined;
      if (props.ccMode) setGlobalSelectedRegion({});
      return;
    }
    const selected = selectedOptions[selectedOptions.length - 1];
    if (!props.ccMode && !selected.isLeaf) return;
    expectedKey.current = selected.value;
    if (props.ccMode) {
      // Set the key/country immediately; geometry is only needed for map focus.
      setGlobalSelectedRegion({ type: 'Feature', properties: { key: selected.value, name: selected.label, country_key: value[0] }, geometry: null });
    }
    const data = await RegionsService.getSubRegions(selected.value, true, true);
    if (requestId.current !== id) return;
    if (!data) { message.error(t('cc.regionError')); return; }
    if (data.geometry) {
      try {
        const [lng, lat] = centroid(data.geometry).geometry.coordinates;
        setGlobalMapCenter({ lat, lng, zoom: value.length === 1 ? 6 : value.length === 2 ? 9 : 13 });
      } catch (error) { console.error('Invalid region geometry', error); }
    }
    if (props.setPlace) props.setPlace(selectedOptions);
  };

  const loadData = async (selectedOptions) => {
    const target = selectedOptions[selectedOptions.length - 1];
    const result = await RegionsService.getSubRegions(target.value);
    if (!result) { message.error(t('cc.regionError')); return; }
    target.children = result.subregions;
    target.isLeaf = !result.subregions.length;
    if (!props.ccMode && target.isLeaf && result.geometry) {
      try {
        const [lng, lat] = centroid(result.geometry).geometry.coordinates;
        setGlobalMapCenter({ lat, lng, zoom: 11 });
      } catch (error) { console.error('Invalid region geometry', error); }
    }
    setRegions((previous) => [...previous]);
  };

  return <Cascader aria-label={t('cc.selectRegion')} bordered={props.bordered} options={regions}
    onChange={onChange} loadData={loadData} changeOnSelect
    value={props.defaultValue?.map((item) => item.value) || selection}
    placeholder={t('cc.selectRegion')} style={{ marginTop: 0 }} />;
};

export default RegionsCascader;
